import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatRoles } from '../utils/roleUtils';
import { changePassword } from '../services/api';

interface ProfileProps {
  skipLayout?: boolean;
}

const Profile: React.FC<ProfileProps> = ({ skipLayout = false }) => {
  const { user, login, isSystemAdmin, isTenantAdmin, isTenantLevelAdmin } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const isDark = theme === 'dark';
  const canChangePassword = !isSystemAdmin && !!user && (isTenantAdmin || isTenantLevelAdmin || user.type === 'user');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 40px 10px 12px',
    fontSize: '15px',
    borderRadius: '6px',
    border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
    background: isDark ? '#0f172a' : '#ffffff',
    color: isDark ? '#f1f5f9' : '#1e293b',
    boxSizing: 'border-box',
  };

  useEffect(() => {
    if (location.hash !== '#change-password' || !canChangePassword) return;
    const timer = window.setTimeout(() => {
      document.getElementById('change-password')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [location.hash, canChangePassword]);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }
    try {
      setSaving(true);
      const response = await changePassword(currentPassword, newPassword);
      const nextToken = response.data.data?.token;
      if (nextToken && user) {
        login(nextToken, user);
      }
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      <div className="card profile-card" style={{ height: canChangePassword ? 'auto' : 'calc(100vh - 285px)' }}>
        <h3 className="profile-section-title">Personal Information</h3>

        <div className="profile-grid">
          <div className="profile-field">
            <span className="profile-label">Name</span>
            <span className="profile-value">{user?.name || user?.username}</span>
          </div>

          {user?.email && (
            <div className="profile-field">
              <span className="profile-label">Email</span>
              <span className="profile-value">{user.email}</span>
            </div>
          )}

          {user?.phone && (
            <div className="profile-field">
              <span className="profile-label">Phone</span>
              <span className="profile-value">{user.phone}</span>
            </div>
          )}

          <div className="profile-field">
            <span className="profile-label">Role</span>
            <span className="profile-value" style={{ textTransform: 'capitalize' }}>
              {isSystemAdmin
                ? 'System Administrator'
                : isTenantLevelAdmin
                  ? 'Admin'
                  : isTenantAdmin
                  ? 'Tenant Administrator'
                  : user?.roles && user.roles.length > 0
                    ? formatRoles(user.roles)
                    : user?.role || 'No role assigned'}
            </span>
          </div>

          {user?.tenantId && (
            <div className="profile-field">
              <span className="profile-label">Tenant</span>
              <span className="profile-value">{user.tenantName || user.tenantId}</span>
            </div>
          )}

          <div className="profile-field">
            <span className="profile-label">User Type</span>
            <span className="profile-value" style={{ textTransform: 'capitalize' }}>
              {isSystemAdmin
                ? 'System Administrator'
                : isTenantLevelAdmin
                  ? 'Admin'
                  : isTenantAdmin
                    ? 'Tenant Administrator'
                    : user?.type?.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </div>

      {canChangePassword && (
        <div id="change-password" className="card profile-card">
          <h3 className="profile-section-title">Change Password</h3>
          {error && <div className="error-message">{error}</div>}
          {success && (
            <div className="success-message" style={{ backgroundColor: '#d4edda', color: '#155724', padding: '10px 14px', borderRadius: '6px', fontSize: '13px' }}>
              {success}
            </div>
          )}
          <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '420px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Current password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: isDark ? '#94a3b8' : '#64748b',
                  }}
                >
                  {showCurrent ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>New password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: isDark ? '#94a3b8' : '#64748b',
                  }}
                >
                  {showNew ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                style={{ ...inputStyle, paddingRight: '12px' }}
              />
            </div>
            <div>
              <button type="submit" className="btn-submit" disabled={saving}>
                {saving ? 'Saving...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      )}

      {user?.permissions && user.permissions.length > 0 && (
        <div className="card profile-card">
          <h3 className="profile-section-title">Your Permissions</h3>
          <div className="profile-permissions-grid">
            {user.permissions.map((perm) => (
              <div key={perm.id} className="profile-permission-pill">
                {perm.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {isSystemAdmin && (
        <div className="card profile-card profile-system-admin-banner">
          <h4 className="profile-system-admin-title">System Administrator</h4>
          <p className="profile-system-admin-text">
            You have full access to all features and resources in the system.
          </p>
        </div>
      )}
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default Profile;
